<template>
  <footer class="pagination-wrapper">
    <ul v-show="hasPages" class="pagination">
      <li v-show="!isFirstPage">
        <button class="pagination-icon" aria-label="Previous Page" @click="prevPage">
          <i class="material-symbols-outlined" aria-hidden="true">navigate_before</i>
        </button>
      </li>
      <li v-show="!isFirstPage">
        <button class="pagination-icon" aria-label="First Page" @click="goToPage(1)">
          <i class="material-symbols-outlined" aria-hidden="true">first_page</i>
        </button>
      </li>
      <!-- show pages based on offset -->
      <template v-for="page in totalPages">
        <li
          v-if="page < currentPage + offset && page > currentPage - offset"
          :key="`page-${page}`"
          :class="{ active: page === currentPage }"
        >
          <button v-if="page !== currentPage" :aria-label="`Page ${page}`" @click="goToPage(page)">
            {{ page }}
          </button>
          <span v-else>{{ page }}</span>
        </li>
      </template>
      <li v-show="!isLastPage">
        <button class="pagination-icon" aria-label="Last Page" @click="goToPage(totalPages)">
          <i class="material-symbols-outlined" aria-hidden="true">last_page</i>
        </button>
      </li>
      <li v-show="!isLastPage">
        <button class="pagination-icon" aria-label="Next Page" @click="nextPage">
          <i class="material-symbols-outlined" aria-hidden="true">navigate_next</i>
        </button>
      </li>
    </ul>
  </footer>
</template>

<script setup lang="ts">
/**
 * Built to work with Laravel Pagination output
 */
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import _debounce from 'lodash/debounce'
import { bp } from '@/constants'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    currentPage: number
    totalPages: number
    totalItems: number
    perPage: number
  }>(),
  {},
)

/** NON REACTIVE **/
const offsetDefault = 3
const offsetMobile = 2

/** EMITS **/
const emit = defineEmits(['page-update'])

/** STATE **/
// used to restrict page numbers shown
const offset = ref(offsetDefault)

/** COMPUTED **/
const hasPages = computed(() => props.totalPages > 1)
const isFirstPage = computed(() => props.currentPage === 1)
const isLastPage = computed(() => props.currentPage === props.totalPages)

/** LIFECYCLE **/
onBeforeMount(() => {
  window.addEventListener('resize', handleResize)
  handleResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

/** METHODS **/
function nextPage() {
  if (props.currentPage === props.totalPages) {
    return
  }

  emit('page-update', props.currentPage + 1)
}

function prevPage() {
  if (props.currentPage === 1) {
    return
  }

  emit('page-update', props.currentPage - 1)
}

function goToPage(page: number) {
  if (page < 1 || page > props.totalPages || props.currentPage === page) {
    return
  }

  emit('page-update', page)
}

const handleResize = _debounce(() => {
  offset.value = window.innerWidth > bp['x-small'] ? offsetDefault : offsetMobile
}, 50)
</script>
